/* =============================================================================
   UTILS / DOM — UI-Plumbing
   =============================================================================
   Sammlung kleiner DOM-Helper, die App-weit gebraucht werden:
   Modal-Open/Close, Tab-Refresh, Edit-Guard für Realtime-Defer,
   Browser-Push-Notification.
============================================================================ */

/** Schließt das Modal-Overlay (entfernt die `active`-Klasse). */
export function closeModal() {
  const ov = document.getElementById('modal-overlay');
  if (!ov) return;
  ov.classList.remove('active');
  // S207-HOTFIX 5 — transiente Plan-Panel-Breite (sizePlanPanel) zurücksetzen, sonst
  // erbt das nächste Modal im geteilten #modal-overlay die schmale Picker-Breite.
  const panel = ov.querySelector('[data-plan-min-w]');
  if (panel) {
    panel.style.width = '';
    panel.style.maxWidth = '';
    delete panel.dataset.planMinW;
    delete panel.dataset.planMaxPx;
    delete panel.dataset.planMaxVw;
    delete panel.dataset.planChrome;
  }
}

/** Backdrop-Click-Handler: Modal schließen, wenn auf den Hintergrund geklickt wird. */
export function closeModalOnBackdrop(e) {
  if (e && e.target && e.target.id === 'modal-overlay') closeModal();
}

/**
 * Re-render des aktiven Admin-Tabs.
 * Findet den aktiven Tab über den DOM und ruft `window.switchTab(name)` auf.
 *
 * Selektor-Strategie (in Reihenfolge geprüft):
 *   1. `[data-subtab].active` — neue Hybrid-Navigation seit Session 20 / Phase 16
 *      (admin-subtabs in `views/admin/shell.js`).
 *   2. `.admin-tabs button.active` — alte flache Tab-Bar, Fallback für ältere
 *      Views die noch nicht migriert sind.
 *
 * Bugfix Session 22: bis hier prüfte rerenderActiveTab NUR den alten
 * Selektor. Seit Phase 16 existiert der nicht mehr → Realtime-Events kamen
 * an, aber das Dashboard/Reservierungs-Tab refreshte nicht ohne F5.
 * Symptom: neue Reservierungen + Bestellungen tauchten erst nach Browser-
 * Refresh auf, obwohl Subscribe + Cache-Patch korrekt liefen.
 */
export function rerenderActiveTab() {
  const subtabActive = document.querySelector('[data-subtab].active');
  const tab = (subtabActive && subtabActive.dataset && subtabActive.dataset.subtab)
    || (() => {
      const legacy = document.querySelector('.admin-tabs button.active');
      return legacy && legacy.dataset && legacy.dataset.tab;
    })();
  if (tab && typeof window.switchTab === 'function') window.switchTab(tab);
}

/** True, wenn der User gerade aktiv in ein Input/Textarea/Select tippt. */
export function isUserEditing() {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

/** True, wenn ein Modal/Overlay gerade offen ist. */
export function isAnyModalOpen() {
  return !!document.querySelector(
    '.modal.open, .modal-overlay.open, .modal-overlay.active, [role="dialog"][aria-modal="true"]'
  );
}

/* =============================================================================
   S224 CUT B — Globaler ESC-Close fürs geteilte #modal-overlay
   =============================================================================
   Ein EINMALIG gebundener keydown-Listener: ESC schließt das geteilte
   #modal-overlay via closeModal() — gleiches Verhalten wie ×/Backdrop. Der
   createDarkOverlay-Pfad (src/views/ui/overlay.js) bringt sein EIGENES ESC auf
   SEPARATEN Overlay-Elementen mit → kein Doppel (dieser Handler wirkt NUR auf
   #modal-overlay). Das Lock-Overlay (#operator-lock-overlay) und die PIN-Pads
   sind eigene Elemente → unberührt.

   Ausnahme: Pflicht-Modale ohne Ausweg außer der Aktion (Geräte-Onboarding
   „Gerät benennen") markieren sich mit `data-modal-no-esc="1"` → ESC schließt
   sie NICHT (sonst bliebe ein unregistriertes Gerät ohne Weg zurück außer
   Reload). Das Attribut ist die EINE Quelle — neue Pflicht-Modale setzen es,
   dom.js muss ihre Interna nicht kennen.
============================================================================ */

/** Pure (Vitest): darf ESC das geteilte #modal-overlay schließen? Nur wenn es
 *  offen (`active`) ist UND das offene Modal nicht als nicht-schließbar
 *  (`data-modal-no-esc`) markiert ist. */
export function shouldEscCloseSharedModal(overlayActive, hasNoEscModal) {
  return !!overlayActive && !hasNoEscModal;
}

function _handleModalEscKeydown(e) {
  if (!e || e.key !== 'Escape') return;
  const ov = document.getElementById('modal-overlay');
  const overlayActive = !!(ov && ov.classList.contains('active'));
  const hasNoEscModal = !!(ov && ov.querySelector('[data-modal-no-esc]'));
  if (shouldEscCloseSharedModal(overlayActive, hasNoEscModal)) {
    closeModal();
  }
}

let _modalEscBound = false;
/** Bindet den globalen ESC-Close-Listener EINMALIG (idempotent, aus bootstrap). */
export function bindGlobalModalEsc() {
  if (_modalEscBound || typeof document === 'undefined') return;
  _modalEscBound = true;
  document.addEventListener('keydown', _handleModalEscKeydown);
}

/** Test-Hook: Handler direkt aufrufbar + Bind-Guard zurücksetzen. */
export function _handleModalEscForTest(e) { _handleModalEscKeydown(e); }
export function _resetModalEscForTest() { _modalEscBound = false; }

/**
 * Optionale Browser-Push-Notification bei neuen Reservierungen.
 * Funktioniert nur, wenn der User authentifiziert ist und vorher
 * Notification-Permission erteilt hat.
 */
export function maybeShowPushNotification(r) {
  if (!window.App || !window.App.auth || !window.App.auth.isAuthenticated()) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification('Neue Reservierung', {
      body: `${r.name} · ${r.guests} Pers. · ${r.date} ${r.time}`,
      tag: 'reservation-' + r.id,
      icon: undefined
    });
  } catch (_e) { /* ignore */ }
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    closeModal,
    closeModalOnBackdrop,
    rerenderActiveTab,
    isUserEditing,
    isAnyModalOpen,
    maybeShowPushNotification,
    bindGlobalModalEsc
  });
}
